import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {
  launchCamera,
  launchImageLibrary,
  type Asset,
  type ImagePickerResponse,
} from 'react-native-image-picker';
import {useTranslation} from 'react-i18next';

import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {FormCard} from '@/shared/ui/form';
import {Avatar} from '@/shared/ui/avatar';
import {spacing} from '@/shared/ui/theme';

/**
 * The face a terminal enrols from. The phone captures (camera) or picks (gallery) a
 * JPEG; we hand up the bare base64 (no `data:` prefix — matching what fetchPersonnel
 * returns and what the Avatar/data-URI helper expects). The terminal builds the
 * recognition template itself from this image on the next sync (enroll_from_avatar);
 * the phone never computes a face_template.
 *
 * The image is downscaled to ≤1280px at q0.8 so the sync payload stays small (the
 * terminal downscales to ≤1280 anyway). Removing the photo emits '' — but a removal
 * WIPES the enrolled face, so the parent must only pass '' when the user asked for it,
 * never as a default on a metadata-only edit.
 */
const PICK_OPTS = {
  mediaType: 'photo',
  maxWidth: 1280,
  maxHeight: 1280,
  quality: 0.8,
  includeBase64: true,
} as const;

const styles = StyleSheet.create({
  portrait: {alignItems: 'center', paddingVertical: spacing(2)},
  buttons: {flexDirection: 'row', gap: spacing(2)},
  button: {flex: 1},
  remove: {alignSelf: 'center'},
});

export function FaceCaptureField({
  name,
  value,
  onChange,
}: {
  /** Person name — drives the initials fallback when there is no photo. */
  name: string;
  /** Current avatar as bare base64, or '' when none. */
  value: string;
  onChange: (base64: string) => void;
}) {
  const {t} = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const consume = (res: ImagePickerResponse) => {
    if (res.didCancel) {
      return;
    }
    if (res.errorCode) {
      setError(
        t(
          res.errorCode === 'permission'
            ? 'personnel.facePermission'
            : 'personnel.faceError',
        ),
      );
      return;
    }
    const asset: Asset | undefined = res.assets?.[0];
    if (asset?.base64) {
      setError(null);
      onChange(asset.base64);
    } else {
      setError(t('personnel.faceError'));
    }
  };

  const pick = async (source: 'camera' | 'library') => {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res =
        source === 'camera'
          ? await launchCamera(PICK_OPTS)
          : await launchImageLibrary(PICK_OPTS);
      consume(res);
    } catch {
      setError(t('personnel.faceError'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormCard title={t('personnel.facePhoto')} hint={t('personnel.faceHint')}>
      {/* Портрет по центру, под ним два равных глагола: карточка стоит в боковой
          колонке шириной с телефон, и строка «портрет слева, кнопки справа» там
          не помещалась ни на мониторе, ни на телефоне. */}
      <View style={styles.portrait}>
        <Avatar name={name} base64={value || null} size={112} />
      </View>
      <View style={styles.buttons}>
        <AppButton
          title={t('personnel.faceTake')}
          tone="outline"
          size="sm"
          icon="camera"
          onPress={() => void pick('camera')}
          loading={busy}
          style={styles.button}
        />
        <AppButton
          title={t('personnel.facePick')}
          tone="outline"
          size="sm"
          icon="image"
          onPress={() => void pick('library')}
          disabled={busy}
          style={styles.button}
        />
      </View>
      {/* Removing the photo WIPES the enrolled face on the next sync, so it stays a
          ghost until it is deliberately reached for. */}
      {value ? (
        <AppButton
          title={t('personnel.faceRemove')}
          tone="ghost"
          size="sm"
          icon="trash"
          onPress={() => {
            setError(null);
            onChange('');
          }}
          disabled={busy}
          style={styles.remove}
        />
      ) : null}
      {error ? <Banner tone="error" title={error} /> : null}
    </FormCard>
  );
}
